import React, { useCallback, useState } from "react";
import { StyleSheet, View } from "react-native";

import OnboardingImageGrid from "./Components/PublicAlbumPicsScreen/OnboardingImageGrid";
import OnboardingBaseScreen from "./Components/OnboardingBaseScreen";
import OnboardingImageUploadCard from "./Components/PublicAlbumPicsScreen/OnboardingImageUploadCard";

import type { AppUserSession } from "@/schemas/auth/session";
import NoteCard from "@/Components/Cards/NoteCard";
import { Spacings } from "@/styling/spacings";
import {
  publicImagesUploadPrompts,
  publicImagesUploadPromptsCount,
} from "@/utils/onboarding";

type Props = {
  session: AppUserSession;
};

function OnboardingPublicAlbumPicsScreen({
  session,
}: Props): React.JSX.Element {
  const [filledImageSlots, setFilledImageSlots] = useState<boolean[]>(
    new Array(publicImagesUploadPromptsCount).fill(false),
  );
  const canContinue = filledImageSlots.filter(Boolean).length >= 3;

  const handleImageSlotFilled = useCallback(
    (index: number, hasImage: boolean) => {
      setFilledImageSlots((prev) => {
        const newSlots = [...prev];
        newSlots[index] = hasImage;
        return newSlots;
      });
    },
    [],
  );

  const renderImageOrUploadPromptCard = (index: number): React.JSX.Element => {
    const prompt = publicImagesUploadPrompts[index];

    if (!prompt) {
      return <View />; // Fallback for invalid index
    }

    return (
      <OnboardingImageUploadCard
        session={session}
        prompt={prompt}
        onImageUploaded={(hasImage) => handleImageSlotFilled(index, hasImage)}
      />
    );
  };

  const stageMainComponent: React.JSX.Element = (
    <View style={styles.container}>
      <OnboardingImageGrid renderImage={renderImageOrUploadPromptCard} />
      <View style={styles.noteCardContainer}>
        <NoteCard
          text={
            "💡 Pro tip: Share a variety of photos — they help others get a complete picture of who you are and make it easier to start conversations.\n\n" +
            "😊 Genuine smiles in photos get 2x more positive responses.\n\n" +
            "👀 Keep public album photos SFW. For other types of photos, use your private album in the app."
          }
        />
      </View>
    </View>
  );

  return (
    <OnboardingBaseScreen
      session={session}
      stageMainComponent={stageMainComponent}
      canContinue={canContinue}
      title="Show Your Many Sides"
      subtitle="Different photos help people see the real, multifaceted you."
      buttonLabel={canContinue ? "Finish" : "Upload At Least 3 Photos"}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  noteCardContainer: {
    marginTop: Spacings.s3,
    marginHorizontal: Spacings.s1,
  },
});

export default OnboardingPublicAlbumPicsScreen;
